import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Home, ArrowRight, Search, FileText, Users } from "lucide-react";

/**
 * Branded 404. Sets a noindex,follow meta tag directly and adds an X-Robots
 * hint via meta http-equiv so crawlers don't index this URL. The React
 * router serves this for every unknown path; hosting-level 404 status is a
 * separate concern (see server.py fallback if wired).
 */
export default function NotFound() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Page not found — FastPrototype.ai";
    const setMeta = (name, content) => {
      let el = document.head.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("robots", "noindex,follow");
    setMeta("description", "The page you're looking for doesn't exist. Head back home or start a new MVP project.");
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="not-found-page">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4 py-24">
        <div className="max-w-xl w-full text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-100 border border-zinc-200 text-xs font-bold uppercase tracking-widest text-zinc-600 mb-6">
            <Search size={12} /> 404
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-zinc-950 leading-tight">
            We couldn't find that page
          </h1>
          <p className="mt-5 text-zinc-600 text-base leading-relaxed">
            The link may be broken, the page may have moved, or the URL may be mistyped. Try one of the routes below.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Link to="/" data-testid="btn-404-home"
              className="inline-flex items-center justify-center px-6 py-3 bg-zinc-950 text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors">
              <Home size={14} className="mr-2" /> Back to home
            </Link>
            <Link to="/build" data-testid="btn-404-build"
              className="inline-flex items-center justify-center px-6 py-3 bg-white border border-zinc-200 text-zinc-800 rounded-full text-sm font-semibold hover:border-zinc-400 hover:bg-zinc-50 transition-colors">
              Start a new project <ArrowRight size={14} className="ml-2" />
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
            <Link to="/about" data-testid="link-404-about" className="bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 rounded-2xl p-4 transition-colors">
              <FileText size={16} className="text-zinc-700 mb-2" />
              <div className="font-semibold text-zinc-950 text-sm">About</div>
              <div className="text-xs text-zinc-500 mt-1">How we build MVPs</div>
            </Link>
            <Link to="/promoters" data-testid="link-404-promoters" className="bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 rounded-2xl p-4 transition-colors">
              <Users size={16} className="text-zinc-700 mb-2" />
              <div className="font-semibold text-zinc-950 text-sm">Promoters</div>
              <div className="text-xs text-zinc-500 mt-1">Earn 15% referring founders</div>
            </Link>
            <Link to="/legal" data-testid="link-404-legal" className="bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 rounded-2xl p-4 transition-colors">
              <FileText size={16} className="text-zinc-700 mb-2" />
              <div className="font-semibold text-zinc-950 text-sm">Legal</div>
              <div className="text-xs text-zinc-500 mt-1">Terms & delivery boundaries</div>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
